// Companion/useScrollEdgeFade.ts
import { useCallback, useEffect, useState, type MutableRefObject } from 'react';

/** Within this many pixels of the end, the list counts as fully scrolled. */
const AT_END_PX = 4;

/** Which way the container scrolls: down the page, or sideways. */
type ScrollAxis = 'x' | 'y';

/**
 * Whether a scroll container has more content past its visible end edge.
 *
 * The shared body of the two hooks below; `axis` picks which end edge is
 * measured. Re-measured on scroll, when the container resizes, and when rows
 * are added or removed — the three things that change whether there is more.
 *
 * The returned `ref` is a callback ref, because the container it watches is
 * not always mounted with its owner (the thread renders an empty state first)
 * and only a callback ref says when it arrives. A caller that also needs the
 * node itself — `ChatThread` scrolls it — passes its own ref as `externalRef`
 * and has it kept up to date.
 */
const useScrollEdgeFadeAxis = <T extends HTMLElement>(
  axis: ScrollAxis,
  externalRef?: MutableRefObject<T | null>
): {
  ref: (node: T | null) => void;
  hasMore: boolean;
  hasPrevious: boolean;
} => {
  const [element, setElement] = useState<T | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const ref = useCallback(
    (node: T | null) => {
      if (externalRef) externalRef.current = node;
      setElement(node);
    },
    [externalRef]
  );

  useEffect(() => {
    if (!element) return;

    const measure = () => {
      const scrolled = axis === 'x' ? element.scrollLeft : element.scrollTop;

      const remaining =
        axis === 'x'
          ? element.scrollWidth - element.clientWidth - scrolled
          : element.scrollHeight - element.clientHeight - scrolled;

      setHasMore(remaining > AT_END_PX);
      setHasPrevious(scrolled > AT_END_PX);
    };

    measure();
    element.addEventListener('scroll', measure, { passive: true });

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);

    resizeObserver?.observe(element);

    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(element, { childList: true, subtree: true });

    return () => {
      element.removeEventListener('scroll', measure);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [axis, element]);

  return { ref, hasMore, hasPrevious };
};

/**
 * Whether a scroll container has more content past its visible edges.
 *
 * The conversation list and the message thread use it to fade their bottom
 * edge only while there is something to scroll to (ORA-628): content that
 * fits, or is scrolled to its end, shows its last line whole rather than
 * half-faded for no reason. A list that is also scrolled away from its start
 * — the video filters — fades its top edge on the same terms.
 */
export const useScrollEdgeFade = <T extends HTMLElement>(
  externalRef?: MutableRefObject<T | null>
): {
  ref: (node: T | null) => void;
  hasMoreBelow: boolean;
  hasMoreAbove: boolean;
} => {
  const { ref, hasMore, hasPrevious } = useScrollEdgeFadeAxis<T>(
    'y',
    externalRef
  );

  return { ref, hasMoreBelow: hasMore, hasMoreAbove: hasPrevious };
};

/**
 * The mask that fades a vertical scroll container at whichever edge has more
 * (the step rail's cue, at a content-sized 2.5rem): nothing when it is all
 * in view. Literal class strings, so Tailwind's scanner finds them.
 */
export const verticalEdgeFadeClass = (
  hasMoreAbove: boolean,
  hasMoreBelow: boolean
): string | false =>
  hasMoreAbove && hasMoreBelow
    ? '[mask-image:linear-gradient(to_bottom,transparent,#000_2.5rem,#000_calc(100%_-_2.5rem),transparent)]'
    : hasMoreAbove
      ? '[mask-image:linear-gradient(to_bottom,transparent,#000_2.5rem)]'
      : hasMoreBelow &&
        '[mask-image:linear-gradient(to_bottom,#000_calc(100%_-_2.5rem),transparent)]';

/**
 * Whether a sideways scroll container has more content past its right edge.
 *
 * The chip rows use it on a phone, where the suggestions and the follow-ups
 * are one scrollable line rather than a block of wrapped rows: the fade is the
 * only cue that there are more chips, so it must be on while — and only while
 * — there are.
 */
export const useScrollEdgeFadeX = <T extends HTMLElement>(
  externalRef?: MutableRefObject<T | null>
): {
  ref: (node: T | null) => void;
  hasMoreRight: boolean;
} => {
  const { ref, hasMore } = useScrollEdgeFadeAxis<T>('x', externalRef);
  return { ref, hasMoreRight: hasMore };
};
